// src/pages/PlansList.tsx
import React, { useEffect, useState } from "react";
import Header from "components/Header";
import Footer from "components/Footer";
import { getImageCandidates, lpBackgroundSrc } from "../lib/okumanAssets";

/* ========= 画像キー候補（サーバ差異を吸収） ========= */
const IMG_URL_KEYS = [
  "imageUrl",
  "image",
  "image_path",
  "imagePath",
  "thumbnailUrl",
  "thumbnail",
  "thumb",
  "photo",
  "picture",
  "img",
  "planImageUrl",
  "plan_image_url",
  "plan_image",
];

/* ========= APIベース：末尾の / と /api を自動除去 ========= */
const RAW_API_BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (process as any).env?.REACT_APP_API_BASE_URL ||
  "";
const API_BASE = (RAW_API_BASE || "")
  .replace(/\/+$/g, "")
  .replace(/\/api$/i, "");
const API_PREFIX = API_BASE ? `${API_BASE}/api/public` : "/api/public";

/* ========= 相対URL → フルURL補完 ========= */
const resolveImg = (u?: string) => {
  if (!u) return "";
  if (u.startsWith("/")) return API_BASE ? `${API_BASE}${u}` : u;
  return u;
};

const pickFirst = (obj: any, keys: string[]) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

/* ========= 型 ========= */
type Plan = {
  id: number | string;
  name: string;
  point?: number | string;
  price?: number;
  buyPt?: string;
  buyPoint?: number;
  [key: string]: any;
};

// 数値系のゆるいパーサ
const toNumber = (v: any): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^\d]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

/* ========= 説明フィールドを拾う（ISO日時は除外） ========= */
const DESC_KEYS = [
  "description",
  "descriptionText",
  "description_text",
  "detailText",
  "detailsText",
  "details",
  "planDescription",
  "descriptionShort",
  "summary",
  "outline",
  "desc",
  "detail",
  "message",
];

const isIsoDateTime = (s: string) =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(s);

const pickDescription = (obj: any): string => {
  for (const k of DESC_KEYS) {
    const v = obj?.[k];
    if (typeof v !== "string") continue;
    const s = v.trim();
    if (!s) continue;
    if (isIsoDateTime(s)) continue;
    return s;
  }
  return "";
};

/* ========= ページング ========= */
const PER_PAGE = 20;

export default function PlansList() {
  const [items, setItems] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const r = await fetch(`${API_PREFIX}/plans`, {
          cache: "no-store",
          credentials: "include",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);

        const json = await r.json();
        const list = Array.isArray(json?.items)
          ? json.items
          : Array.isArray(json)
          ? json
          : [];
        setItems(list);
        setPage(1); // 取得し直したら1ページ目へ
      } catch (e: any) {
        setErr(e?.message || "読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // items 変更時にページ番号補正
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const startIndex = (page - 1) * PER_PAGE;
  const pagedItems = items.slice(startIndex, startIndex + PER_PAGE);

  return (
    <main className="lp">
      <Header />

      <div
        style={{
          minHeight: "100vh",
          backgroundImage: `url("${lpBackgroundSrc()}")`,
          backgroundRepeat: "repeat",
          backgroundAttachment: "fixed",
          padding: "20px 0 40px",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {loading && <p style={{ textAlign: "center" }}>読み込み中…</p>}
          {err && (
            <p style={{ color: "crimson", textAlign: "center" }}>
              エラー: {err}
            </p>
          )}
          {!loading && !err && items.length === 0 && (
            <p style={{ textAlign: "center" }}>
              現在、公開中のプランはありません。
            </p>
          )}

          <div
            style={{
              display: "grid",
              gap: 24,
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            }}
          >
            {pagedItems.map((p) => (
              <Card key={String(p.id)} plan={p} />
            ))}
          </div>

          {/* ページネーション */}
          {!loading && items.length > 0 && (
            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 12,
                fontSize: 13,
              }}
            >
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page <= 1 ? "#f5f5f5" : "#fff",
                  cursor: page <= 1 ? "default" : "pointer",
                }}
              >
                前へ
              </button>
              <span>
                {page} / {totalPages} ページ（全 {items.length} 件）
              </span>
              <button
                type="button"
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={page >= totalPages}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page >= totalPages ? "#f5f5f5" : "#fff",
                  cursor: page >= totalPages ? "default" : "pointer",
                }}
              >
                次へ
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </main>
  );
}

function Card({ plan }: { plan: Plan }) {
  // 1) サーバ返却の画像URL優先
  const serverImg = resolveImg(pickFirst(plan, IMG_URL_KEYS));

  // 2) 無ければ従来の候補
  const idNum = Number(plan.id);
  const safeId = Number.isFinite(idNum) ? idNum : 0;
  const cands = getImageCandidates({ id: safeId, name: plan.name || "" });
  const fallbacks = cands.map(resolveImg);

  const all = [serverImg, ...fallbacks].filter(Boolean);

  const [idx, setIdx] = useState(0);
  const src = all[Math.min(idx, all.length - 1)] || "/assets/no-image.webp";

  // 販売ポイント：各フィールドから柔軟に取得
  const rawPt = (() => {
    const direct = toNumber(plan.point);
    if (direct > 0) return direct;

    const price = toNumber(plan.price);
    if (price > 0) return price;

    const buyPt = toNumber(plan.buyPt);
    if (buyPt > 0) return buyPt;

    const buyPoint = toNumber(plan.buyPoint);
    if (buyPoint > 0) return buyPoint;

    return 0;
  })();
  const priceText = rawPt > 0 ? `${rawPt.toLocaleString()}pt` : "-";

  const desc = pickDescription(plan);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 4px 18px rgba(0, 0, 0, 0.08)",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          background: "#eef1f4",
          borderRadius: 12,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <img
          src={src}
          onError={() => setIdx((v) => Math.min(v + 1, all.length - 1))}
          alt={plan.name}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <div
          style={{
            background: "#1e6dc8",
            color: "#fff",
            textAlign: "center",
            padding: "8px 4px",
            fontWeight: 800,
            borderRadius: 4,
            fontSize: 16,
          }}
        >
          {rawPt === 0 ? "販売ポイント 無料" : `販売ポイント ${priceText}`}

        </div>

        <div
          style={{
            marginTop: 6,
            background: "#fff",
            border: "1px solid #1e6dc8",
            borderRadius: 4,
            padding: "8px 10px",
            fontSize: 14,
            lineHeight: 1.6,
            minHeight: 40,
            boxSizing: "border-box",
          }}
        >
          {desc}
        </div>

        <div style={{ marginTop: 10, textAlign: "center" }}>
          <a
            href={`/plans/${plan.id}`}
            style={{
              display: "inline-block",
            }}
          >
            <img
              src="/assets/member/okuman_botan2.webp"
              alt="詳細を見る"
              style={{
                width: "80%",
                maxWidth: "260px",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </a>
        </div>
      </div>
    </div>
  );
}
